import React, { useMemo, useState } from 'react'
import { observer } from 'mobx-react'
import { Alert, FormControlLabel, Radio, RadioGroup } from '@mui/material'

import { useStores } from 'admin/hooks'

const formatAreaCounts = (areaCounts = {}) => {
  return Object.entries(areaCounts)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([areaName, count]) => `${areaName} (${count})`)
    .join(', ')
}

const formatDetailedSequence = (sequence = []) => {
  return sequence
    .map((stop) => {
      const timestamp = stop.timestamp ? ` @ ${stop.timestamp}` : ''
      const booking = stop.bookingId ? ` #${stop.bookingId}` : ''
      const job = stop.jobId ? ` (${stop.jobId})` : ''
      return `${stop.areaName}${timestamp}${booking}${job}`
    })
    .join(' -> ')
}

const isDeliveryStop = (stop = {}) => {
  const candidate = `${stop.jobType || stop.job || stop.type || stop.tagType || ''}`.toLowerCase()
  return candidate.includes('delivery')
}

const Summary = () => {
  const { reportStore } = useStores()
  const [viewMode, setViewMode] = useState('collapsed')

  const groupedRoutes = useMemo(() => {
    return (reportStore.driverRouteGroups || []).map((group) => {
      const weekday = group.weekday
        ? `${group.weekday.charAt(0).toUpperCase()}${group.weekday.slice(1)}`
        : 'Unknown day'
      return {
        key: `${group.weekday}-${group.driverId || 'unassigned'}`,
        title: `${weekday} ${group.driverName || 'Unassigned'}`,
        routeText: group.routeText || '-',
        detailedSequenceText: formatDetailedSequence(group.sequence),
        stopCountText: formatAreaCounts(group.areaCounts),
        sequence: group.sequence || [],
      }
    })
  }, [reportStore.driverRouteGroups])

  const kpiMetrics = useMemo(() => {
    const areaCounts = {}
    const deliveryAreaCounts = {}
    let totalStops = 0

    groupedRoutes.forEach((group) => {
      ;(group.sequence || []).forEach((stop) => {
        const normalizedArea = `${stop.areaName || ''}`.trim()
        if (!normalizedArea) return
        totalStops += 1
        areaCounts[normalizedArea] = (areaCounts[normalizedArea] || 0) + 1
        if (isDeliveryStop(stop)) {
          deliveryAreaCounts[normalizedArea] = (deliveryAreaCounts[normalizedArea] || 0) + 1
        }
      })
    })

    const uniqueAreas = Object.keys(areaCounts).length
    const areasWithMultipleStops = Object.values(areaCounts).filter((count) => count > 1).length
    const sameAreaRepeatStops = Object.values(areaCounts).reduce(
      (sum, count) => sum + (count > 1 ? count - 1 : 0),
      0,
    )
    const sameAreaRepeatDeliveryStops = Object.values(deliveryAreaCounts).reduce(
      (sum, count) => sum + (count > 1 ? count - 1 : 0),
      0,
    )

    return {
      totalStops,
      uniqueAreas,
      areasWithMultipleStops,
      sameAreaRepeatStops,
      sameAreaRepeatDeliveryStops,
    }
  }, [groupedRoutes])

  const topTransitions = useMemo(() => {
    if (reportStore.driverRouteQuery.mode !== 'aggregated') return []
    const transitionCounts = {}

    groupedRoutes.forEach((group) => {
      const seq = group.sequence || []
      for (let index = 0; index < seq.length - 1; index += 1) {
        const currentArea = `${seq[index]?.areaName || ''}`.trim()
        const nextArea = `${seq[index + 1]?.areaName || ''}`.trim()
        if (currentArea && nextArea) {
          const key = `${currentArea} -> ${nextArea}`
          transitionCounts[key] = (transitionCounts[key] || 0) + 1
        }
      }
    })

    return Object.entries(transitionCounts)
      .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
      .slice(0, 5)
  }, [groupedRoutes, reportStore.driverRouteQuery.mode])

  if (!groupedRoutes.length) {
    return (
      <Alert
        severity="info"
        sx={{ mb: 2 }}
      >
        No route summary available for selected filters.
      </Alert>
    )
  }

  return (
    <div style={{ marginBottom: '1rem' }}>
      <div style={{ display: 'flex', gap: '1rem', marginBottom: '0.75rem', flexWrap: 'wrap' }}>
        <div>
          <strong>Total stops:</strong> {kpiMetrics.totalStops || 0}
        </div>
        <div>
          <strong>Unique areas:</strong> {kpiMetrics.uniqueAreas || 0}
        </div>
        <div>
          <strong>Areas with multiple stops:</strong> {kpiMetrics.areasWithMultipleStops || 0}
        </div>
        <div>
          <strong>Repeated stops (same area):</strong> {kpiMetrics.sameAreaRepeatStops || 0}
        </div>
        <div>
          <strong>Repeated delivery stops:</strong> {kpiMetrics.sameAreaRepeatDeliveryStops || 0}
        </div>
      </div>
      {reportStore.driverRouteQuery.mode === 'aggregated' && (
        <div style={{ marginBottom: '0.75rem' }}>
          <div style={{ fontWeight: 700 }}>Top transitions (area to area)</div>
          {topTransitions.length ? (
            topTransitions.map(([transition, count]) => (
              <div key={transition}>
                {transition}: {count}
              </div>
            ))
          ) : (
            <div>-</div>
          )}
        </div>
      )}
      <RadioGroup
        row
        value={viewMode}
        onChange={(event) => setViewMode(event.target.value)}
        sx={{ mb: 1 }}
      >
        <FormControlLabel
          value="collapsed"
          control={<Radio size="small" />}
          label="Collapsed view"
        />
        <FormControlLabel
          value="detailed"
          control={<Radio size="small" />}
          label="Detailed sequence"
        />
      </RadioGroup>

      {groupedRoutes.map((item) => (
        <div
          key={item.key}
          style={{ marginBottom: '0.75rem' }}
        >
          <div style={{ fontWeight: 700 }}>{item.title}</div>
          <div>
            {viewMode === 'collapsed'
              ? item.routeText
              : item.detailedSequenceText || item.routeText}
          </div>
          <div style={{ color: '#6b7280', fontSize: '0.875rem' }}>{item.stopCountText || '-'}</div>
        </div>
      ))}
    </div>
  )
}

export default observer(Summary)
